import React from "react";
import { colors2, getVal, icons, transition, UI_BODY_SEMIBOLD_SM } from "oolib";
import styled from "styled-components";
import TooltipV2 from "@/components/UI/Tooltip";
import { TitleInputWithDefault } from "../../utils/TitleInputWithDefault";
import { ConfigForm_SectionWrapper } from "../../WidgetMetricCard/ConfigForm/ConfigForm_SectionWrapper";
import { WidgetHeader } from "../WIDGET_ELEM_ConfigForm";
import { BarChartConfigProvider, useBarChartConfig } from "./ConfigForm_Context";
import { ConfigForm_Dimension1Section, Dimension1AddFieldButton } from "./ConfigForm_Dimension1Section";
import { ConfigForm_ConfigureSection } from "./ConfigForm_ConfigureSection";
import { ConfigForm_Dimension2Section, Dimension2AddFieldButton } from "./ConfigForm_Dimension2Section";
import { ConfigForm_DisplaySection } from "./ConfigForm_DisplaySection";
import { ConfigForm_FiltersSection } from "./ConfigForm_FiltersSection";
import { DATA_KEYS } from "./DATA_KEYS";
import { useWidgetFilterControls } from "../../utils/useWidgetFilterControls";
import { getWidgetHasDimensions } from "../../utils/getWidgetContentTypes";

const { ArrowsClockwise } = icons;

const StyledFlipSection = styled.div`
  border-bottom: 1px solid ${colors2.grey10};
  margin: 0 -1.6rem;
  padding: 0 1.6rem 1.4rem 1.6rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  ${transition('background 0.15s')}

  &:hover {
    background: ${({ $disabled }) => $disabled ? 'transparent' : colors2.grey05};
  }

  & > * {
    opacity: ${({ $disabled }) => $disabled ? 0.4 : 1};
  }
`;

const SwapDimensionsButton = () => {
  const { widgetConfig, handleFlipDimensions } = useBarChartConfig();
  const dim1 = getVal(widgetConfig, DATA_KEYS.dimension1);
  const dim2 = getVal(widgetConfig, DATA_KEYS.dimension2);
  const isExpression = dim1?.isExpression;
  const isDisabled = !dim1 || !dim2 || isExpression;

  const tooltipText = !dim1
    ? "Select data first"
    : !dim2
      ? "Select breakdown first"
      : isExpression
        ? "Swap not available for expressions"
        : "Swap data and breakdown dimensions";

  return (
    <TooltipV2 text={tooltipText} placement="top">
      <StyledFlipSection
        $disabled={isDisabled}
        onClick={isDisabled ? undefined : handleFlipDimensions}
      >
        <ArrowsClockwise size={16} color={colors2.grey80} weight="bold" />
        <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>
          Swap Data and Breakdown
        </UI_BODY_SEMIBOLD_SM>
      </StyledFlipSection>
    </TooltipV2>
  );
};

export const BarChartConfigForm = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
  showNumberInputsInOptions = true,
  showDimension2Input = true,
  dimensionsInputPropsConfig = {},
  widgetIcon,
  widgetTitle,
}) => {
  const widgetType = thisWidgetConfig?.comp || 'WidgetBarChart';
  const hasDimensions = getWidgetHasDimensions(thisWidgetConfig, widgetType);
  const { filterHeaderProps, filterControlProps } = useWidgetFilterControls({
    hasDimensions
  });
  return (
    <BarChartConfigProvider
      thisWidgetConfig={thisWidgetConfig}
      setConfig={setConfig}
      underEditWidgetIdx={underEditWidgetIdx}
      showNumberInputsInOptions={showNumberInputsInOptions}
      showDimension2Input={showDimension2Input}
      dimensionsInputPropsConfig={dimensionsInputPropsConfig}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
        <WidgetHeader title={widgetTitle} type="Chart" icon={widgetIcon} />

      <ConfigForm_SectionWrapper>
        <TitleInputWithDefault
          widgetConfig={thisWidgetConfig}
          widgetType="WidgetBarChart"
          setConfig={setConfig}
          underEditWidgetIdx={underEditWidgetIdx}
          titlePath={DATA_KEYS.field1}
          tagLabel="WIDGET TITLE"
          placeholder="Update custom label"
        />
      </ConfigForm_SectionWrapper>

      <ConfigForm_SectionWrapper icon="Database" title="Data" headerRight={<Dimension1AddFieldButton />}>
        <ConfigForm_Dimension1Section />
      </ConfigForm_SectionWrapper>

      <ConfigForm_ConfigureSection />

      {showDimension2Input && (
        <>
          <SwapDimensionsButton />
          <ConfigForm_SectionWrapper icon="Flask" title="Breakdown" headerRight={<Dimension2AddFieldButton />}>
            <ConfigForm_Dimension2Section />
          </ConfigForm_SectionWrapper>
        </>
      )}



        <ConfigForm_DisplaySection />

        <ConfigForm_SectionWrapper
          icon="Funnel"
          title="Filters"
          {...filterHeaderProps}
        >
          <ConfigForm_FiltersSection {...filterControlProps} />
        </ConfigForm_SectionWrapper>
      </div>
    </BarChartConfigProvider>
  );
};
